import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send, CheckCircle2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { localApi } from "../../lib/localApi";
import { formatError } from "../../lib/errors";

const field = "w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-brass/60 focus:ring-2 focus:ring-brass/20 transition-colors";

export function DemoForm() {
  const [f, setF] = useState({ name: "", agency: "", email: "", phone: "", properties_count: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await localApi.post("/public/demo-request", {
        ...f, properties_count: f.properties_count === "" ? undefined : Number(f.properties_count),
        phone: f.phone || undefined, message: f.message || undefined, ref: localStorage.getItem("cn_ref") || undefined, website: f.website || undefined,
      });
      setSent(true);
    } catch (err) { toast.error(formatError(err, "Envoi impossible, réessayez dans un instant")); }
    finally { setBusy(false); }
  };

  return (
    <section id="demo" className="border-y border-white/5 bg-white/[0.02]" data-testid="landing-demo">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-24 grid gap-12 lg:grid-cols-[1fr_1.1fr] items-start">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-brass">Démo personnalisée</p>
          <h2 className="display mt-4 text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">20 minutes avec une gérante de conciergerie, pas un commercial.</h2>
          <p className="mt-4 text-sm md:text-base text-slate-300 leading-relaxed">
            Nous vous montrons Casanéo sur de vrais logements, avec vos questions : synchro des plateformes, encaissements, relevés propriétaires, ménage.
            Réponse sous 24 h ouvrées.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-slate-300">
            {["Visio de 20 minutes, à l'heure qui vous convient", "Conseils pour migrer depuis Lodgify, Smoobu, Excel…", "Aucun engagement"].map((t) => <li key={t} className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-brass" /> {t}</li>)}
          </ul>
        </div>
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.45 }} className="rounded-2xl border border-white/10 bg-[#071e42]/70 p-6 sm:p-8">
          {sent ? (
            <div className="text-center py-10" data-testid="landing-demo-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
              <h3 className="display text-2xl text-white mt-4">Demande bien reçue</h3>
              <p className="text-sm text-slate-300 mt-2">Nous vous recontactons sous 24 h ouvrées à l'adresse indiquée pour fixer la démo.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="landing-demo-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input required minLength={2} placeholder="Votre nom" aria-label="Votre nom" value={f.name} onChange={set("name")} className={field} data-testid="demo-name" />
                <input required placeholder="Nom de l'agence / conciergerie" aria-label="Nom de l'agence / conciergerie" value={f.agency} onChange={set("agency")} className={field} data-testid="demo-agency" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input required type="email" placeholder="E-mail professionnel" aria-label="E-mail professionnel" value={f.email} onChange={set("email")} className={field} data-testid="demo-email" />
                <input type="tel" placeholder="Téléphone (facultatif)" aria-label="Téléphone (facultatif)" value={f.phone} onChange={set("phone")} className={field} data-testid="demo-phone" />
              </div>
              <input type="number" min={0} placeholder="Nombre de logements gérés" aria-label="Nombre de logements gérés" value={f.properties_count} onChange={set("properties_count")} className={field} data-testid="demo-properties" />
              <textarea rows={3} placeholder="Vos enjeux : plateformes utilisées, outil actuel, questions…" aria-label="Vos enjeux : plateformes utilisées, outil actuel, questions…" value={f.message} onChange={set("message")} className={field} data-testid="demo-message" />
              <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
              <button type="submit" disabled={busy} data-testid="demo-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brass hover:bg-brass/90 text-[#0b1a35] font-semibold px-6 py-3.5 text-sm transition-transform hover:scale-[1.01] disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Demander ma démo
              </button>
              <p className="text-[11px] text-slate-500 text-center">Vos coordonnées servent uniquement à organiser la démo. Jamais revendues.</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
